import Avatar from '@app/src/components/Ui/Avatar'
import useOrganizationSettings from '@app/src/hooks/useOrganizationSettings'
import { AccountTreeOutlined, PublicOutlined, VerifiedOutlined } from '@mui/icons-material'
import { Box, Divider, ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import AiScreeningLimitAlert from './AiScreeningLimitAlert'
import AiScreeningOptimizedAlert from './AiScreeningOptimizedAlert'
import AnswerAcceptanceSettings from './AnswerAcceptanceSettings'
import WebPageCaptureSettings from './WebPageCaptureSettings'

const modelDisplayName = (model?: string) => {
  const [family, version, ...variant] = model?.split('-') ?? []
  if (!family) return ''
  return [[family.toUpperCase(), version].filter(Boolean).join('-'), ...variant].join(' ')
}

const AiScreeningSettings: React.FC = () => {
  const { formatMessage } = useIntl()
  const { settings: organizationSettings } = useOrganizationSettings()
  const { openAiScreeningModel } = useFlags()

  const remainingProviderScreenings = organizationSettings?.remainingProviderScreenings ?? 0

  return (
    <>
      <Stack spacing={2}>
        {remainingProviderScreenings <= 0 && <AiScreeningLimitAlert />}
        <Stack bgcolor="common.white" borderRadius={2} p={4} spacing={3}>
          <Typography variant="subtitle1">{formatMessage({ id: 'aiScreening.creditsOverview.title' })}</Typography>
          <Typography bgcolor="grey.100" py={2} px={2} borderRadius={2}>
            {formatMessage(
              { id: 'aiScreening.creditsOverview.remaining' },
              {
                remaining: remainingProviderScreenings,
                allowed: organizationSettings?.allowedProviderScreenings ?? 0,
              },
            )}
          </Typography>
        </Stack>
      </Stack>

      <AnswerAcceptanceSettings />

      <WebPageCaptureSettings />

      <Stack bgcolor="common.white" borderRadius={2} p={4} spacing={2}>
        <Typography variant="subtitle1">{formatMessage({ id: 'aiScreening.sources.title' })}</Typography>
        <Box borderRadius={2} p={2}>
          <Stack spacing={2}>
            <ListItem disablePadding disableGutters component="div" sx={{ padding: 0 }}>
              <ListItemIcon>
                <Avatar sx={({ palette }) => ({ bgcolor: palette.secondary.main })}>
                  <VerifiedOutlined />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={formatMessage({ id: 'aiScreening.sources.companyWebsite' })}
                secondary={formatMessage({ id: 'aiScreening.sources.companyWebsiteDescription' })}
              />
            </ListItem>
            <Divider />
            <ListItem disablePadding disableGutters component="div" sx={{ padding: 0 }}>
              <ListItemIcon>
                <Avatar sx={({ palette }) => ({ bgcolor: palette.secondary.main })}>
                  <AccountTreeOutlined />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={formatMessage({ id: 'aiScreening.sources.parentCompany' })}
                secondary={formatMessage({ id: 'aiScreening.sources.parentCompanyDescription' })}
              />
            </ListItem>
            <Divider />
            <ListItem disablePadding disableGutters component="div" sx={{ padding: 0 }}>
              <ListItemIcon>
                <Avatar sx={({ palette }) => ({ bgcolor: palette.secondary.main })}>
                  <PublicOutlined />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={formatMessage({ id: 'aiScreening.sources.trustedPublicSources' })}
                secondary={formatMessage({ id: 'aiScreening.sources.trustedPublicSourcesDescription' })}
              />
            </ListItem>
            <ListItem disablePadding disableGutters component="div" sx={{ padding: 0 }}>
              <ListItemText
                primary={formatMessage({ id: 'aiScreening.sources.modelTitle' })}
                secondary={modelDisplayName(openAiScreeningModel)}
              />
            </ListItem>
          </Stack>
        </Box>
        <AiScreeningOptimizedAlert />
      </Stack>
    </>
  )
}

export default AiScreeningSettings
